import { authQueries, useProfileQuery, UseQueryOptionsObject } from 'src/api/queries';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { pickObject } from 'src/helpers/pickObject';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import {
  profileClient,
  ProfileResponse,
  UpdateProfileRequest,
} from 'src/lib/services/api/profile-api';
import { RequestFormObject } from 'src/lib/services/api/request-api';
import { reactQueryClient } from 'src/providers/ReactQueryClientProvider';
import { useMemo } from 'react';

const STATUS = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
} as const;

const useProfile = (options?: UseQueryOptionsObject<ProfileResponse>) => {
  const { data: profile, ...profileQuery } = useProfileQuery(options);

  const company = profile?.company;
  const meta = company?.meta;

  const {
    hasActiveSubscriptions,
    hasTrialingSubscriptions,
    hasFailedSubscriptions,
  } = useMemo(
    () => {
      let active = false;
      let trialing = false;
      let failedSubscription = false;
      
      // Single Mapping for check subscriptions statuses
      if (company?.subscriptions?.length) {
        for (const s of company.subscriptions) {
          if (s.status === STATUS.TRIALING) {
            trialing = true;
            active = true; // trialing is also considered active
          } else if (s.status === STATUS.ACTIVE) {
            active = true;
          } else if (s.status === STATUS.PAST_DUE) {
            failedSubscription = true;
          }
          
          // Early exit if we've found both conditions
          if (active && trialing && failedSubscription) break;
        }
      }
      
      return {
        hasActiveSubscriptions: active,
        hasTrialingSubscriptions: trialing,
        hasFailedSubscriptions: failedSubscription,
      };
    },
    [company?.subscriptions],
  );

  const fullName = useMemo(
    () => joinExistingValues([profile?.first_name, profile?.last_name], ' '),
    [profile?.first_name, profile?.last_name],
  );

  const isLoaded = !profileQuery.isLoading && profileQuery.isSuccess,
    hasBrands = !!meta?.has_brands,
    hasSubscriptions = !!meta?.has_subscriptions,
    hasProvidedCompanyInformation =
      !!company?.name &&
      !!company?.billing_email &&
      !!company?.address &&
      !!company?.city &&
      !!company?.country_code &&
      !!company?.zip_code,
    hasVerifiedEmail = !!profile?.email_verified_at,
    hasRequests = !!meta?.has_requests,
    submittedRequests = meta?.submitted_requests || 0,
    attentionRequiredRequests = meta?.attention_required_requests || 0,
    canCreateRequest = hasSubscriptions || !hasRequests,
    isImpersonated = !!AuthUtils.getPayload()?.impersonator,
    isOnboarded = hasVerifiedEmail && hasSubscriptions,
    eligibleForTrial = !(meta?.all_subscriptions_count || 0 > 0),
    companyHasBasicInformationFilled =
      !!company?.brand_business_name &&
      !!company?.monthly_video_count &&
      !!company?.success_with_video_team &&
      !!company?.audience_video_feel &&
      !!company?.brand_links,
    hasOnlyReelquickSubscriptions = !!(
      !!company?.subscriptions?.length &&
      company?.subscriptions.every((s) => s.plan?.slug === 'reelquick')
    );
  

  const updateProfile = async (data: Partial<UpdateProfileRequest>) => {
    const current = pickObject(profile!, ['first_name', 'last_name', 'email', 'phone_number']);

    await profileClient.update({
      ...current,
      ...data,
    });

    profileQuery.setQueryData(data);
  };

  const canSubmitRequest = (values?: Partial<RequestFormObject>): boolean => {
    if (hasSubscriptions) {
      return true;
    }

    return values?.status === 'draft';
  };

  const stopImpersonating = async (): Promise<string> => {
    return profileClient.stopImpersonating();
  };

  return {
    ...(profile ?? {}),
    hasBrands,
    hasSubscriptions,
    hasActiveSubscriptions,
    hasFailedSubscriptions,
    hasTrialingSubscriptions,
    hasOnlyReelquickSubscriptions,
    hasRequests,
    submittedRequests,
    attentionRequiredRequests,
    canCreateRequest,
    isImpersonated,
    fullName,
    isLoaded,
    hasProvidedCompanyInformation,
    hasVerifiedEmail,
    isOnboarded,
    eligibleForTrial,
    companyHasBasicInformationFilled,
    query: profileQuery,
    updateProfile,
    canSubmitRequest,
    stopImpersonating,
  };
};

export const getProfileData = () =>
  reactQueryClient.getQueryData<ProfileResponse>([authQueries.profile]);

export { useProfile };
